<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import type { Plugin } from 'chart.js'
import { Roles } from '@/constants.ts'
import dashboardHttpService, { type CategoryStat } from '@/services/http/dashboardHttpService.ts'
import registrationsHttpService from '@/services/http/registrationsHttpService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t } = useI18n()
const toast = useToast()

const statsQuery = useQuery({
  queryKey: ['admin', 'dashboard', 'statistics'],
  queryFn: () => dashboardHttpService.getStatistics(),
})

// Воронка заявок по статусам жизненного цикла: «Всего» + каждый статус (порядок с бэка).
// Дисплей-боксы (на Аналитике нет списка для фильтра, в отличие от Документов).
const funnelBoxes = computed(() => {
  const stats = statsQuery.data.value?.statusBreakdown ?? []
  const total = stats.reduce((sum, s) => sum + s.count, 0)
  return [
    { key: 'total', label: t('analytics.funnel.total'), count: total },
    ...stats.map((s) => ({ key: s.code, label: s.name ?? s.code, count: s.count })),
  ]
})

// Выгрузка реестра регистраций — те же эндпоинты, что на странице «Регистрации»
// (без фильтров: выгружается весь реестр активного отбора бэкенда). Каждая кнопка
// дизейблится/крутится только на время своего запроса.
const exporting = ref(false)
const exportingPdf = ref(false)

async function onExport() {
  exporting.value = true
  try {
    await registrationsHttpService.exportRegistrations()
  } catch {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('registrations.toasts.exportFailed'),
      life: 5000,
    })
  } finally {
    exporting.value = false
  }
}

async function onExportPdf() {
  exportingPdf.value = true
  try {
    await registrationsHttpService.exportRegistrationsPdf()
  } catch {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('registrations.toasts.exportPdfFailed'),
      life: 5000,
    })
  } finally {
    exportingPdf.value = false
  }
}

// Рост регистраций по дням наложением на ОДНОЙ оси, столбцы одинаковой ширины:
// накопительный итог (тёмно-зелёный) сзади, дневной прирост (светло-зелёный) спереди.
// Накопительный всегда ≥ дневного, поэтому его тёмный «хвост» всегда виден над дневным —
// столбик читается как стопка: светлый низ = за день, тёмный верх = было до этого дня.
// grouped:false — оба центрируются на одном дне (наложение). Порядок важен: в bar-графиках
// chart.js рисует ПЕРВЫЙ датасет поверх, поэтому дневной идёт первым (спереди).
const chartData = computed(() => {
  const points = statsQuery.data.value?.dailyDynamics ?? []
  return {
    labels: points.map((p) => p.date),
    datasets: [
      {
        type: 'bar',
        label: t('analytics.registrations.perDay'),
        data: points.map((p) => p.count),
        backgroundColor: '#34d399',
        grouped: false,
        categoryPercentage: 0.8,
        barPercentage: 0.8,
      },
      {
        type: 'bar',
        label: t('analytics.registrations.cumulative'),
        data: points.map((p) => p.cumulative),
        backgroundColor: '#059669',
        grouped: false,
        categoryPercentage: 0.8,
        barPercentage: 0.8,
      },
    ],
  }
})

const chartOptions = {
  maintainAspectRatio: false,
  plugins: { legend: { position: 'top' as const } },
  // Y от −80: в служебной полосе (−80..0) плагин печатает две строки под столбцами —
  // сверху дневной прирост, под ним накопительный. Деления < 0 прячем (полоса служебная).
  scales: {
    y: {
      min: -80,
      ticks: {
        callback: (value: number | string) => (Number(value) < 0 ? '' : value),
      },
    },
  },
}

// Подписи значений прямо на столбцах — выровнены по определению (рисуются в координатах баров):
// накопительный над верхом столбца, дневной прирост со знаком «+» по центру светлого сегмента.
const valueLabelsPlugin: Plugin<'bar'> = {
  id: 'valueLabels',
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx
    const yScale = chart.scales.y
    const dayMeta = chart.getDatasetMeta(0)
    const labels = chart.data.labels ?? []
    ctx.save()
    ctx.textAlign = 'center'
    ctx.font = '700 14px sans-serif'
    for (let i = 0; i < labels.length; i++) {
      const day = Number(chart.data.datasets[0].data[i] ?? 0)
      const cum = Number(chart.data.datasets[1].data[i] ?? 0)
      const x = dayMeta.data[i].x
      // Две строки в служебной полосе под нулём: сверху дневной прирост, под ним накопительный.
      ctx.fillStyle = '#047857'
      ctx.fillText(`+${day}`, x, yScale.getPixelForValue(-28))
      ctx.fillStyle = '#065f46'
      ctx.fillText(String(cum), x, yScale.getPixelForValue(-62))
    }
    ctx.restore()
  },
}

const chartPlugins = [valueLabelsPlugin]

// Ключ строки таблицы = пол+возрастной диапазон (групповой микс — отдельная строка).
function rowKey(c: CategoryStat): string {
  const g = c.isGroupRace ? 'GROUP' : (c.genderCode ?? 'GROUP')
  return `${g}|${c.ageMin}|${c.ageMax ?? ''}`
}

function ageLabel(c: CategoryStat): string {
  return c.ageMax == null ? `${c.ageMin}+` : `${c.ageMin}–${c.ageMax}`
}

function genderLabel(c: CategoryStat): string {
  if (c.isGroupRace) return t('analytics.registrations.group')
  // Компактная подпись из справочника: первая буква имени пола (Мужской→М, Женский→Ж).
  return c.genderName ? c.genderName.charAt(0) : (c.genderCode ?? '')
}

interface PivotRow {
  key: string
  label: string
  // Дистанция (км) → число регистраций в ячейке. Ключ есть только у доступных категорий
  // (бэкенд отдаёт все категории события, включая пустые с 0): нет ключа → категории нет → «x».
  counts: Record<number, number>
  // Дистанция → слоты команд (для групповых категорий); иначе ключа нет.
  slots: Record<number, number>
  total: number
  totalSlots: number | null
}

// Колонки таблицы — фактические дистанции события (по возрастанию), не хардкод 6/13:
// при добавлении дистанции колонка появится сама и Σ останется согласованным.
const distances = computed<number[]>(() => {
  const set = new Set<number>()
  for (const c of statsQuery.data.value?.categoryBreakdown ?? []) set.add(c.distanceKm)
  return [...set].sort((a, b) => a - b)
})

function distanceHeader(d: number): string {
  return t('category.km', { km: d })
}

// Пивот плоской разбивки в сетку: строки = union (пол, возраст), колонки = дистанции.
const pivotRows = computed<PivotRow[]>(() => {
  const cats = statsQuery.data.value?.categoryBreakdown ?? []
  const map = new Map<string, PivotRow>()
  for (const c of cats) {
    const key = rowKey(c)
    let row = map.get(key)
    if (!row) {
      row = {
        key,
        label: `${genderLabel(c)} ${ageLabel(c)}`,
        counts: {},
        slots: {},
        total: 0,
        totalSlots: null,
      }
      map.set(key, row)
    }
    row.counts[c.distanceKm] = (row.counts[c.distanceKm] ?? 0) + c.count
    row.total += c.count
    if (c.slotsTotal != null) {
      row.slots[c.distanceKm] = (row.slots[c.distanceKm] ?? 0) + c.slotsTotal
      row.totalSlots = (row.totalSlots ?? 0) + c.slotsTotal
    }
  }
  return [...map.values()]
})

// Ячейка: «x» — категории на этой дистанции нет (недоступна); «-» — категория есть, но участников
// нет (0); «занято / слоты» — групповая категория; иначе число участников.
function cellText(row: PivotRow, d: number): string {
  const count = row.counts[d]
  if (count == null) return 'x'
  const slots = row.slots[d]
  if (slots != null) return `${count} / ${slots}`
  return count === 0 ? '-' : String(count)
}

function totalText(row: PivotRow): string {
  if (row.totalSlots != null) return `${row.total} / ${row.totalSlots}`
  return row.total === 0 ? '-' : String(row.total)
}

// Итоги по колонкам-дистанциям (по числу регистраций). Σ строки сходится с total.
const distanceTotals = computed<Record<number, number>>(() => {
  const totals: Record<number, number> = {}
  for (const row of pivotRows.value) {
    for (const d of distances.value) totals[d] = (totals[d] ?? 0) + (row.counts[d] ?? 0)
  }
  return totals
})

const grandTotal = computed(() => pivotRows.value.reduce((s, r) => s + r.total, 0))
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <!-- Шапка: заголовок слева, выгрузка реестра — справа (как на странице «Регистрации»). -->
    <div class="flex flex-wrap items-center justify-between gap-3 w-full">
      <h1 class="text-xl font-bold">{{ t('analytics.title') }}</h1>
      <div class="flex items-center gap-2">
        <Button
          class="btn-excel"
          size="small"
          :label="t('registrations.exportExcel')"
          icon="pi pi-file-excel"
          :loading="exporting"
          :disabled="exporting"
          data-test="export-excel"
          @click="onExport"
        />
        <Button
          class="btn-pdf"
          size="small"
          :label="t('registrations.exportPdf')"
          icon="pi pi-file-pdf"
          :loading="exportingPdf"
          :disabled="exportingPdf"
          data-test="export-pdf"
          @click="onExportPdf"
        />
      </div>
    </div>

    <!-- Воронка заявок по статусам (жизненный цикл): под заголовком, над статистикой. Дисплей. -->
    <div
      v-if="funnelBoxes.length"
      class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-10 gap-2 w-full"
      data-test="registrations-funnel"
    >
      <div
        v-for="box in funnelBoxes"
        :key="box.key"
        class="flex flex-col gap-1 rounded border border-surface-200 dark:border-surface-700 p-3"
        :data-test="`funnel-${box.key}`"
      >
        <span class="text-xs text-surface-500">{{ box.label }}</span>
        <span class="text-2xl font-bold">{{ box.count }}</span>
      </div>
    </div>

    <Card>
      <template #title>{{ t('analytics.registrations.dynamicsTitle') }}</template>
      <template #content>
        <div class="h-80">
          <Chart
            type="bar"
            :data="chartData"
            :options="chartOptions"
            :plugins="chartPlugins"
            class="h-full"
          />
        </div>
      </template>
    </Card>

    <Card>
      <template #title>{{ t('analytics.registrations.title') }}</template>
      <template #content>
        <DataTable :value="pivotRows" :loading="statsQuery.isLoading.value" class="w-full">
          <template #empty>{{ t('analytics.registrations.empty') }}</template>
          <Column field="label" :header="t('analytics.registrations.genderAge')" />
          <Column v-for="d in distances" :key="d" :header="distanceHeader(d)">
            <template #body="{ data }">{{ cellText(data, d) }}</template>
          </Column>
          <Column :header="t('analytics.registrations.total')">
            <template #body="{ data }"
              ><span class="font-bold">{{ totalText(data) }}</span></template
            >
          </Column>
          <ColumnGroup type="footer">
            <Row>
              <Column :footer="t('analytics.registrations.totalRow')" />
              <Column v-for="d in distances" :key="d" :footer="String(distanceTotals[d] ?? 0)" />
              <Column :footer="String(grandTotal)" />
            </Row>
          </ColumnGroup>
        </DataTable>
      </template>
    </Card>
  </section>
</template>

<style scoped>
/* Outlined: прозрачный фон, граница и текст — фирменным зелёным Excel (ненавязчиво). */
.btn-excel {
  background: transparent !important;
  border-color: #217346 !important;
  color: #217346 !important;
}
.btn-excel:hover {
  background: rgba(33, 115, 70, 0.08) !important;
  border-color: #217346 !important;
}
/* Outlined: прозрачный фон, граница и текст — Adobe-красным (#FA0F00). */
.btn-pdf {
  background: transparent !important;
  border-color: #fa0f00 !important;
  color: #fa0f00 !important;
}
.btn-pdf:hover {
  background: rgba(250, 15, 0, 0.08) !important;
  border-color: #fa0f00 !important;
}
</style>
